import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import { useStoreUrl } from "@/hooks/useStoreUrl";
import { useFavorites } from "@/hooks/useFavorites";
import { Button } from "@/components/ui/button";
import { Store, User, Package, LogOut, Settings, UserCircle, Search, Heart } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MiniCart from "./MiniCart";

export default function StoreHeader() {
  const { user, profile, signOut, isAdmin } = useAuth();
  const { settings } = useStoreSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { storeUrl, basePath } = useStoreUrl();
  const { count: favCount } = useFavorites();
  const [searchOpen, setSearchOpen] = useState(false);

  const isActivePath = (path: string, exact = false) => {
    return exact ? location.pathname === path : location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/80">
      <div className="container mx-auto flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 gap-2">
        {/* Logo */}
        <Link to={storeUrl("/")} className="flex items-center gap-2 min-w-0 shrink-0">
          {settings.logo_url ? (
            <img
              src={settings.logo_url}
              alt={settings.store_name}
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl object-contain"
            />
          ) : (
            <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Store className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
          )}
          <span className="text-sm sm:text-lg font-bold text-foreground truncate max-w-[100px] sm:max-w-none">
            {settings.store_name}
          </span>
        </Link>

        {/* Desktop Search Bar */}
        <div className="hidden md:flex flex-1 max-w-md mx-4">
          <button
            onClick={() => navigate(storeUrl("/catalog?focus=search"))}
            className="w-full flex items-center gap-2 h-10 px-4 rounded-xl border border-border/60 bg-muted/30 text-sm text-muted-foreground hover:border-primary/30 hover:bg-muted/50 transition-colors"
          >
            <Search className="h-4 w-4" />
            <span>Buscar produtos, marcas…</span>
          </button>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {[
            { to: storeUrl("/"), label: "Início", exact: true },
            { to: storeUrl("/catalog"), label: "Produtos" },
            { to: storeUrl("/favorites"), label: "Favoritos", badge: favCount, requiresAuth: true },
          ].map(({ to, label, exact, badge, requiresAuth }) => {
            const active = isActivePath(to, exact);
            return (
              <Link
                key={to}
                to={requiresAuth && !user ? storeUrl("/auth") : to}
                className={`relative rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "text-primary bg-primary/5"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                {label}
                {badge != null && badge > 0 && (
                  <span className="absolute -top-0.5 -right-1 min-w-[16px] h-4 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center px-1">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </Link>
            );
          })}
          {isAdmin && (
            <Link
              to="/admin"
              className="rounded-lg px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors flex items-center gap-1.5"
            >
              <Settings className="h-3.5 w-3.5" />
              Admin
            </Link>
          )}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Mobile search button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden h-8 w-8"
            onClick={() => navigate(storeUrl("/catalog?focus=search"))}
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Desktop cart only — mobile uses bottom nav */}
          <div className="hidden md:block">
            <MiniCart />
          </div>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 sm:h-9 sm:w-9">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-foreground">
                    {profile?.full_name || "Minha Conta"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(storeUrl("/profile"))}>
                  <UserCircle className="mr-2 h-4 w-4" /> Meu Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(storeUrl("/orders"))}>
                  <Package className="mr-2 h-4 w-4" /> Meus Pedidos
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Settings className="mr-2 h-4 w-4" /> Painel Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    // Cliente logout → loja em modo deslogado (ex: /loja/daff)
                    const target = basePath || "/";
                    window.location.href = target;
                    signOut();
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              size="sm"
              className="rounded-xl shadow-sm h-8 text-xs sm:text-sm px-3"
              onClick={() => navigate(storeUrl("/auth"))}
            >
              Entrar
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
